import { computed, unref, type ComputedRef, type Ref } from "vue";
import type { SimpleDate } from "@wkt/api/employee-calendar/employeeCalendarDTO";
import {
  ProcedureDefinitionClass,
  type FullProcedureDefinitionDTO,
  type FullProcedureRequestDTO,
  type ProcedureTimeDTO,
} from "@wkt/api/procedures";
import {
  type ProcedureRequestDialogFieldEditability,
  type ProcedureRequestDialogMode,
  type ProcedureRequestDialogOpenContext,
  type ProcedureRequestFieldVisibility,
  resolveProcedureRequestDialogOpenContext,
  resolveProcedureRequestFieldEditability,
  resolveProcedureRequestFieldVisibility,
} from "./procedureRequestDialogContract";

type MaybeRef<T> = T | Ref<T> | ComputedRef<T>;

export interface ProcedureRequestFormValues {
  requestTitle: string;
  requestInfo: string;
  fromDate: SimpleDate | null;
  toDate: SimpleDate | null;
  fromHour: ProcedureTimeDTO | null;
  toHour: ProcedureTimeDTO | null;
  quantity: number | null;
  projectId: number | null;
  activityTypeId: number | null;
  hoursBagType: number | null;
  paidAbsenceCategoryId: number | null;
  approverComment: string;
  documentIds: number[];
}

export interface ProcedureRequestFormFlags {
  isCreate: boolean;
  isEdit: boolean;
  isApprove: boolean;
  isReadOnly: boolean;
  isVacationLeave: boolean;
  isVacationByDays: boolean;
  isVacationByHours: boolean;
  showAbsenceCategory: boolean;
  hasExpenseSheet: boolean;
  projectRequired: boolean;
  titleRequired: boolean;
  freeTextRequired: boolean;
  quantityUsesDefault: boolean;
  projectLocked: boolean;
  isLegacyVacationRequest: boolean;
}

export interface ProcedureRequestFormState {
  openContext: ProcedureRequestDialogOpenContext;
  visibility: ProcedureRequestFieldVisibility;
  editability: ProcedureRequestDialogFieldEditability;
  values: ProcedureRequestFormValues;
  flags: ProcedureRequestFormFlags;
  allowedHoursBagTypeIds: number[];
  allowedPaidAbsenceCategoryIds: number[];
}

export interface UseProcedureRequestFormParams {
  request: MaybeRef<FullProcedureRequestDTO | null | undefined>;
  definition: MaybeRef<FullProcedureDefinitionDTO | null | undefined>;
  mode: MaybeRef<ProcedureRequestDialogMode>;
  editable?: MaybeRef<boolean | undefined>;
  asApprover?: MaybeRef<boolean | undefined>;
  deleteOnClose?: MaybeRef<boolean | undefined>;
}

export function useProcedureRequestForm(params: UseProcedureRequestFormParams) {
  const openContext = computed(() =>
    resolveProcedureRequestDialogOpenContext(unref(params.request) || null, {
      mode: unref(params.mode),
      editable: unref(params.editable),
      asApprover: unref(params.asApprover),
      deleteOnClose: unref(params.deleteOnClose),
    })
  );

  const visibility = computed(() =>
    resolveProcedureRequestFieldVisibility(unref(params.definition) || null)
  );

  const editability = computed(() =>
    resolveProcedureRequestFieldEditability(openContext.value, unref(params.request) || null)
  );

  const values = computed<ProcedureRequestFormValues>(() => {
    const request = unref(params.request) || null;
    const definition = unref(params.definition) || null;

    return {
      requestTitle: request?.requestTitle || "",
      requestInfo: request?.requestInfo || "",
      fromDate: request?.fromDate || null,
      toDate: request?.toDate || null,
      fromHour: request?.fromHour || null,
      toHour: request?.toHour || null,
      quantity: request?.quantity ?? definition?.defaultQuantity ?? null,
      projectId: request?.projectId ?? null,
      activityTypeId: request?.activityTypeId ?? definition?.activityTypeId ?? null,
      hoursBagType: request?.hoursBagType ?? null,
      paidAbsenceCategoryId: request?.paidAbsenceCategoryId ?? null,
      approverComment: request?.approverComment || "",
      documentIds: parseNumericList(request?.documentIds),
    };
  });

  const flags = computed<ProcedureRequestFormFlags>(() => {
    const request = unref(params.request) || null;
    const definition = unref(params.definition) || null;
    const mode = openContext.value.mode;
    const isVacationLeave =
      definition?.procedureClass === ProcedureDefinitionClass.VACATION_LEAVE;
    const showAbsenceCategory =
      Boolean(visibility.value.enableAbsenceCategory) &&
      isVacationLeave &&
      !visibility.value.showVacationDays;

    return {
      isCreate: mode === "create",
      isEdit: mode === "edit",
      isApprove: mode === "approve",
      isReadOnly: mode === "read-only",
      isVacationLeave,
      isVacationByDays:
        isVacationLeave &&
        visibility.value.showFromDate &&
        visibility.value.showToDate,
      isVacationByHours:
        isVacationLeave &&
        visibility.value.showFromTime &&
        visibility.value.showToTime,
      showAbsenceCategory,
      hasExpenseSheet: request?.expenseSheetId != null,
      projectRequired:
        visibility.value.showProject && request?.expenseSheetId == null,
      titleRequired: !visibility.value.showProject,
      freeTextRequired:
        visibility.value.showFreeText && request?.expenseSheetId == null,
      quantityUsesDefault:
        definition?.defaultQuantity != null && !visibility.value.showQuantity,
      projectLocked: request?.projectId != null,
      isLegacyVacationRequest:
        hasNonZeroValue(request?.vacationDeltaOld) &&
        hasNonZeroValue(request?.extraDeltaOld),
    };
  });

  const allowedHoursBagTypeIds = computed(() =>
    parseNumericList(unref(params.definition)?.hoursBagTypes)
  );

  const allowedPaidAbsenceCategoryIds = computed(() =>
    parseNumericList(unref(params.definition)?.paidAbsencesCategories)
  );

  const state = computed<ProcedureRequestFormState>(() => ({
    openContext: openContext.value,
    visibility: visibility.value,
    editability: editability.value,
    values: values.value,
    flags: flags.value,
    allowedHoursBagTypeIds: allowedHoursBagTypeIds.value,
    allowedPaidAbsenceCategoryIds: allowedPaidAbsenceCategoryIds.value,
  }));

  return {
    state,
    openContext,
    visibility,
    editability,
    values,
    flags,
    allowedHoursBagTypeIds,
    allowedPaidAbsenceCategoryIds,
  };
}

function parseNumericList(value?: string | null): number[] {
  if (!value?.trim()) {
    return [];
  }

  return value
    .split(",")
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isFinite(item));
}

function hasNonZeroValue(value?: number | null): boolean {
  return value != null && value !== 0;
}
